import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import store from './store';
import { INIT, READY, OPEN, CLOSE, DESTROY } from './STATES';
import InputType from './InputType';
import theme from '../../theme';
// import { styles } from '../../styles/modal.scss';

const isBrowser = typeof window !== 'undefined';
const closest = isBrowser ? require('element-closest') : null;  // eslint-disable-line

function errorLoading(err) {
  console.error('Dynamic page loading failed', err); // eslint-disable-line
}

const fadein = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeout = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const slidein = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-${theme.unit * 2}px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideout = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-${theme.unit * 2}px);
  }
`;

const ModalLayout = styled.div`
  position: absolute;
  z-index: 10;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const ModalContainer = styled.div`
  bottom: 0;
  left: 0;
  overflow: scroll;
  position: absolute;
  right: 0;
  top: 0;
`;

const ModalBackground = styled.div`
  backdrop-filter: blur(${theme.unit}px);
  background: ${theme.colors.modal};
  background-size: cover;
  box-sizing: border-box;
  height: 100%;
  left: 0;
  opacity: 0;
  position: fixed;
  right: 0;
  top: 0;

  ${({ visible }) => visible && `
    animation: ${fadein} 300ms ease forwards 0s;
  `}

  ${({ hidden }) => hidden && `
    animation: ${fadeout} 225ms ease forwards 150ms;
    opacity: 1;
  `}
`;

const ModalView = styled.div`
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
  opacity: 0;
  padding-bottom: ${theme.unit * 10}px;
  padding-left: ${theme.unit * 2}px;
  padding-right: ${theme.unit * 2}px;
  padding-top: ${theme.unit * 10}px;
  position: absolute;
  transform: translateY(-${theme.unit * 2}px);
  transform-origin: top center;
  width: 100%;
  z-index: 1;

  @media screen and (max-width: ${theme.desktop}px) {
    padding-left: ${theme.unit}px;
    padding-right: ${theme.unit}px;
  }

  ${({ open }) => open && `
    animation: ${slidein} 300ms ease forwards 0s;
  `}

  ${({ closed }) => closed && `
    animation: ${slideout} 150ms ease forwards 0s;
    opacity: 1;
    transform: translateY(0);
  `}
`;

const ModalBlock = styled.div`
  margin: 0 auto;
  padding: ${theme.unit * 2}px;
  pointer-events: all;
  position: relative;
  max-width: 100%;
  box-sizing: border-box;

  ${({ size }) => size === 'small' && `
    width: ${theme.unit * 96}px;
  `}

  ${({ size }) => size === 'normal' && `
    width: ${theme.unit * 142}px;
  `}

  ${({ size }) => size === 'wide' && `
    width: 100%;
  `}
`;

const svgCloseIcon = (color = '#fff') =>
  `"data:image/svg+xml,%3Csvg width='44' height='44' xmlns='http://www.w3.org/2000/svg' stroke='${color}' stroke-width='6'%3E%3Cline x1='6' y1='6' x2='38' y2='38' /%3E%3Cline x1='6' y1='38' x2='38' y2='6' /%3E%3C/svg%3E"`;

const ModalClose = styled.div`
  background: ${theme.colors.gray6} url(${svgCloseIcon()}) center center no-repeat;
  background-size: ${theme.unit * 2}px;
  border: 0;
  border-radius: ${theme.unit * 2}px;
  cursor: pointer;
  display: block;
  height: ${theme.unit * 4}px;
  outline: 0;
  pointer-events: all;
  position: absolute;
  right: 0;
  top: 0;
  width: ${theme.unit * 4}px;

  &:hover {
    background-color: ${theme.colors.gray7};
  }

  &:active {
    background-color: ${theme.colors.gray4};
  }
`;

class Modal extends PureComponent {
  static propTypes = {
    loading: PropTypes.bool,
    open: PropTypes.bool,
    closed: PropTypes.bool,
    component: PropTypes.node,
    componentProps: PropTypes.object,
    className: PropTypes.string,
  }

  static defaultProps = {
    loading: false,
    open: false,
    closed: false,
    component: null,
    componentProps: null,
    className: null,
  }

  static childContextTypes = {
    closeModal: PropTypes.func,
  };

  static open(props) {
    PropTypes.checkPropTypes(InputType, props);
    const { component, size } = props;
    store.dispatch({ type: INIT, size });
    if (component instanceof Promise) {
      component
        .then(({ default: Module }) => {
          store.dispatch({
            type: READY,
            component: <Module {...props} />,
          });
          clearTimeout(this.timeout);
          this.timeout = setTimeout(() => store.dispatch({ type: OPEN }), 0);
        }).catch(errorLoading);
    } else if (component instanceof Array) {
      const outer = component[0];
      if (outer) {
        outer
          .then(({ default: Module }) => {
            const inner = component[1];
            inner
              .then(({ default: InnerModule }) => {
                store.dispatch({
                  type: READY,
                  component: <Module {...props}><InnerModule /></Module>,
                });
                clearTimeout(this.timeout);
                this.timeout = setTimeout(() => store.dispatch({ type: OPEN }), 0);
              }).catch(errorLoading);
          }).catch(errorLoading);
      }
    } else {
      store.dispatch({ type: READY, component });
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => store.dispatch({ type: OPEN }), 0);
    }
  }

  static close(e) {
    if (!e || (e.target
      && (
        e.target.id === 'modal-background'
        || e.target.id === 'modal-view'
        || e.target.id === 'modal-close'
      )
    )) {
      store.dispatch({ type: CLOSE });
      // Wait for the animation to finish before removing the component.
      clearTimeout(Modal.timeout);
      Modal.timeout = setTimeout(() => store.dispatch({ type: DESTROY }), 300);
    }
  }

  getChildContext() {
    return {
      closeModal: Modal.close,
    };
  }

  componentDidMount() {
    this.handleClick = Modal.close;
    this.handleHistoryChange = () => {
      store.dispatch({ type: CLOSE });
    };
    this.handleEscClose = e => {
      if (e.key === 'Escape') {
        store.dispatch({ type: CLOSE });
      }
    };
  }

  componentDidUpdate() {
    if (typeof window !== 'undefined') {
      if (this.props.open || this.props.loading) {
        window.addEventListener('keydown', this.handleEscClose);
        window.addEventListener('click', this.handleClick);
        window.addEventListener('popstate', this.handleHistoryChange);
        window.addEventListener('pushstate', this.handleHistoryChange);
      } else {
        window.removeEventListener('click', this.handleClick);
        window.addEventListener('popstate', this.handleHistoryChange);
        window.removeEventListener('pushstate', this.handleHistoryChange);
        window.removeEventListener('keydown', this.handleEscClose);
      }
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('click', this.handleClick);
      window.addEventListener('popstate', this.handleHistoryChange);
      window.removeEventListener('pushstate', this.handleHistoryChange);
      window.removeEventListener('keydown', this.handleEscClose);
    }
  }

  render() {
    const {
      loading,
      open,
      closed,
      component,
      props,
      size,
    } = this.props;
    if (!open && !loading) {
      return null;
    }

    return (
      <ModalLayout id="modal">
        <ModalContainer id="modal-container">
          <ModalBackground
            id="modal-background"
            visible={!closed}
            hidden={closed}
          />
          <ModalView
            id="modal-view"
            open={open && !closed}
            closed={closed}
          >
            <ModalBlock size={size}>
              <ModalClose
                id="modal-close"
                title="Close"
                onClick={Modal.close}
              />
              {component && (
                React.cloneElement(component, {
                  closeModal: Modal.close,
                  ...props,
                })
              )}
            </ModalBlock>
          </ModalView>
        </ModalContainer>
      </ModalLayout>
    );
  }
}

export default compose(
  connect(() => ({ store })),
  connect(state => state),
)(Modal);



// WEBPACK FOOTER //
// ./src/components/stateful/Modal/Modal.js